import { useState, useEffect, useCallback, useRef } from "react";
import { externalgetApi } from "../services/externalApis.tsx";
import { ITEM_META_API } from "../api/ApiModel.tsx";
import { CON_STATUS_ACTIVE } from "../utils/sys/Constants.tsx";

// Types and Interfaces
export interface DropdownOption {
  label: string;
  value: string | number;
  code: string | number;
  [key: string]: any;
}

export interface MetaItem {
  [key: string]: any;
}

export interface ItemMetaResponse {
  brands?: MetaItem[];
  maincategories?: MetaItem[];
  categories?: MetaItem[];
  subcategories?: MetaItem[];
  genericnames?: MetaItem[];
  measuringunits?: MetaItem[];
  packages?: MetaItem[];
  Itemvariantstypes?: MetaItem[];
  itemvariantstypes?: MetaItem[];
  [key: string]: any;
}

export interface VariantTypeOption extends DropdownOption {
  variantDataType: string;
}

export interface ItemMetaRef {
  categories: MetaItem[];
  subcategories: MetaItem[];
  genericnames: MetaItem[];
}

export interface UseDropdownDataReturn {
  isFetchingData: boolean;
  brands: DropdownOption[];
  mainCategories: DropdownOption[];
  categories: DropdownOption[];
  subCategories: DropdownOption[];
  genericNames: DropdownOption[];
  uoms: DropdownOption[];
  packageTypes: DropdownOption[];
  variantTypes: VariantTypeOption[];
  error: string | null;
  fetchCategories: (mainCatCode: string) => void;
  fetchSubCategories: (catCode: string, mainCatCode: string) => void;
  fetchGenericNames: (subCategoryCode: string, mainCategoryCode: string) => void;
  refetch: () => Promise<void>;
}

const formatDropdownResponse = (
  response: any[] | undefined,
  labelKey: string,
  valueKey: string,
  codeKey: string
): DropdownOption[] => {
  if (!response || !Array.isArray(response)) return [];
  return response.map((item: any) => ({
    ...item,
    label: item[labelKey] || item.name || "",
    value: item[valueKey] || item.id || "",
    code: item[codeKey] || item.id || 0,
  }));
};

export const useDropdownData = (
  COMPCODE?: string,
  STAFFMAPLOCODE?: string
): UseDropdownDataReturn => {
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
  const [brands, setBrands] = useState<DropdownOption[]>([]);
  const [mainCategories, setMainCategories] = useState<DropdownOption[]>([]);
  const [categories, setCategories] = useState<DropdownOption[]>([]);
  const [subCategories, setSubCategories] = useState<DropdownOption[]>([]);
  const [genericNames, setGenericNames] = useState<DropdownOption[]>([]);
  const [uoms, setUoms] = useState<DropdownOption[]>([]);
  const [packageTypes, setPackageTypes] = useState<DropdownOption[]>([]);
  const [variantTypes, setVariantTypes] = useState<VariantTypeOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  const itemMetaRef = useRef<ItemMetaRef>({
    categories: [],
    subcategories: [],
    genericnames: [],
  });

  const fetchDropdownData = useCallback(async (): Promise<void> => {
    if (!COMPCODE || !STAFFMAPLOCODE) return;

    const params = {
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      STS: CON_STATUS_ACTIVE,
    };

    try {
      setIsFetchingData(true);
      setError(null);

      const response = await externalgetApi(ITEM_META_API, "GET", params);
      const meta: ItemMetaResponse = Array.isArray(response) ? response[0] : response || {};

      itemMetaRef.current = {
        categories: meta.categories || [],
        subcategories: meta.subcategories || [],
        genericnames: meta.genericnames || [],
      };

      setBrands(
        formatDropdownResponse(meta.brands, "brandname", "brandcode", "brandcode")
      );
      setMainCategories(
        formatDropdownResponse(
          meta.maincategories,
          "maincatname",
          "maincatcode",
          "maincatcode"
        )
      );
      setUoms(
        formatDropdownResponse(
          meta.measuringunits,
          "measuringunit",
          "measuringunitcode",
          "measuringunitcode"
        )
      );
      setPackageTypes(
        formatDropdownResponse(
          meta.packages,
          "packagename",
          "packagecode",
          "packagecode"
        )
      );
      
      const rawVariantTypes = meta.Itemvariantstypes ?? meta.itemvariantstypes ?? [];
      const formatted: VariantTypeOption[] = Array.isArray(rawVariantTypes)
        ? rawVariantTypes
            .map((row: any) => ({
              code: row.VARIANTCODE ?? row.variantcode ?? 0,
              label: (row.VARIANTNAME ?? row.variantname ?? "").trim() || "",
              value: (row.VARIANTNAME ?? row.variantname ?? "").trim() || "",
              variantDataType: row.VARIANTDATATYPE ?? row.variantdatatype ?? "",
            }))
            .filter((t: VariantTypeOption) => t.label || t.value)
        : [];
      setVariantTypes(formatted);
      
      setCategories([]);
      setSubCategories([]);
      setGenericNames([]);
    } catch (err) {
      console.error("Error fetching dropdown data:", err);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setIsFetchingData(false);
    }
  }, [COMPCODE, STAFFMAPLOCODE]);

  const fetchCategories = useCallback((mainCatCode: string): void => {
    if (!mainCatCode) {
      setCategories([]);
      return;
    }
    const { categories: rawCategories } = itemMetaRef.current;
    const filtered = rawCategories.filter(
      (item: any) => String(item.maincatcode) === String(mainCatCode)
    );
    setCategories(
      formatDropdownResponse(filtered, "catname", "catcode", "catcode")
    );
  }, []);

  const fetchSubCategories = useCallback((catCode: string, mainCatCode: string): void => {
    if (!catCode || !mainCatCode) {
      setSubCategories([]);
      return;
    }
    const { subcategories: rawSubcategories } = itemMetaRef.current;
    const filtered = rawSubcategories.filter(
      (item: any) =>
        String(item.catcode) === String(catCode) &&
        String(item.maincatcode) === String(mainCatCode)
    );
    setSubCategories(
      formatDropdownResponse(
        filtered,
        "subcatname",
        "subcatcode",
        "subcatcode"
      )
    );
  }, []);

  const fetchGenericNames = useCallback((subCategoryCode: string): void => {
    if (!subCategoryCode) {
      setGenericNames([]);
      return;
    }
    const { genericnames: rawGenericNames } = itemMetaRef.current;
    const filtered = rawGenericNames.filter(
      (item: any) => String(item.subcatcode) === String(subCategoryCode)
    );
    setGenericNames(
      formatDropdownResponse(
        filtered,
        "genericname",
        "genericcode",
        "genericcode"
      )
    );
  }, []);

  useEffect(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);

  return {
    isFetchingData,
    brands,
    mainCategories,
    categories,
    subCategories,
    genericNames,
    uoms,
    packageTypes,
    variantTypes,
    error,
    fetchCategories,
    fetchSubCategories,
    fetchGenericNames,
    refetch: fetchDropdownData,
  };
};